import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { deerEndpoints } from 'Deer/constants/endpoints';
import { sendRequest } from 'api/sendRequest';
import {
  checkNewStaticDataFeaturesEnabled,
  checkOverviewExplanationsEnabled,
  checkOverviewReviewsExplanationsEnabled,
  checkSandboxStaticDataFeaturesEnabled,
} from 'utils/helpers/featureFlags';

import {
  METRICS_EXPLANATIONS_RESPONSE,
  METRICS_EXPLANATION_REQUEST_BODY,
  REVIEWS_EXPLANATION_REQUEST_BODY,
  REVIEWS_EXPLANATION_SANKEY_REQUEST_BODY,
  REVIEW_EXPLANATIONS_RESPONSE,
} from './types/MetricsTypes';

const fetchMetricsExplanationAPI = async ({ cohortId, group, correlationId }: METRICS_EXPLANATION_REQUEST_BODY) => {
  try {
    const url = deerEndpoints.analytics.explanations;
    // @ts-expect-error send request is not typed
    const res = (await sendRequest({
      url,
      method: 'POST',
      credentials: 'include',
      body: {
        cohortId,
        group,
        correlationId,
      },
    })) as METRICS_EXPLANATIONS_RESPONSE;

    return res?.explanations ?? [];
  } catch (error) {
    console.error('Error fetching metrics', error);
    throw new Error(error);
  }
};

const fetchReviewsExplanationAPI = async ({
  cohortId,
  group,
  correlationId,
  gridLineGraphResponse,
}: REVIEWS_EXPLANATION_REQUEST_BODY) => {
  try {
    const url = deerEndpoints.analytics.reviewsExplanations;
    // @ts-expect-error send request is not typed
    const res = (await sendRequest({
      url,
      method: 'POST',
      credentials: 'include',
      body: {
        cohortId,
        group,
        correlationId,
        gridLineGraphResponse,
      },
    })) as REVIEW_EXPLANATIONS_RESPONSE;

    return res?.explanations ?? [];
  } catch (error) {
    console.error('Error fetching metrics', error);
    throw new Error(error);
  }
};

const fetchReviewsExplanationSankeyAPI = async ({
  cohortId,
  group,
  correlationId,
  sankeyGraphResponse,
}: REVIEWS_EXPLANATION_SANKEY_REQUEST_BODY) => {
  try {
    const url = deerEndpoints.analytics.reviewsExplanationsSankey;
    // @ts-expect-error send request is not typed
    const res = (await sendRequest({
      url,
      method: 'POST',
      credentials: 'include',
      body: {
        cohortId,
        group,
        correlationId,
        sankeyGraphResponse,
      },
    })) as REVIEW_EXPLANATIONS_RESPONSE;

    return res?.explanations ?? [];
  } catch (error) {
    console.error('Error fetching metrics', error);
    throw new Error(error);
  }
};

export const generateMetricsQueryKey = ({ cohortId, group, correlationId }: METRICS_EXPLANATION_REQUEST_BODY) => [
  'metrics',
  cohortId,
  group,
  correlationId,
];

export const useGetOverviewMetricsExplanations = ({
  cohortId,
  group,
  correlationId,
  enabled = true,
}: METRICS_EXPLANATION_REQUEST_BODY) => {
  // Generate a stable query key
  const queryKey = useMemo(
    () => generateMetricsQueryKey({ cohortId, group, correlationId }),
    [cohortId, group, correlationId]
  );

  const isFeatureEnabled =
    checkOverviewExplanationsEnabled() &&
    !checkSandboxStaticDataFeaturesEnabled() &&
    !checkNewStaticDataFeaturesEnabled();
  const placeholderData = isFeatureEnabled ? undefined : null;

  // Memoize the enabled condition
  const queryEnabled = useMemo(() => isFeatureEnabled && enabled, [isFeatureEnabled, enabled]);

  const fetchMetrics = useMemo(
    () => () => fetchMetricsExplanationAPI({ cohortId, group, correlationId }),
    [cohortId, group, correlationId]
  );

  // Use the useQuery hook
  const { data, ...rest } = useQuery({
    queryKey,
    queryFn: fetchMetrics,
    enabled: queryEnabled,
    placeholderData,
  });

  return { data, ...rest };
};

export const useGetOverviewReviewsExplanations = ({
  cohortId,
  group,
  correlationId,
  enabled = true,
  gridLineGraphResponse,
}: REVIEWS_EXPLANATION_REQUEST_BODY) => {
  // Generate a stable query key
  const queryKey = useMemo(
    () => generateMetricsQueryKey({ cohortId, group, correlationId }),
    [cohortId, group, correlationId]
  );

  const isFeatureEnabled =
    checkOverviewExplanationsEnabled() &&
    checkOverviewReviewsExplanationsEnabled() &&
    !checkSandboxStaticDataFeaturesEnabled() &&
    !checkNewStaticDataFeaturesEnabled();
  const placeholderData = isFeatureEnabled ? undefined : null;

  // Memoize the enabled condition
  const queryEnabled = useMemo(() => isFeatureEnabled && enabled, [isFeatureEnabled, enabled]);
  const fetchMetrics = useMemo(
    () => () => fetchReviewsExplanationAPI({ cohortId, group, correlationId, gridLineGraphResponse }),
    [cohortId, group, correlationId, gridLineGraphResponse]
  );

  // Use the useQuery hook
  const { data, ...rest } = useQuery({
    queryKey,
    queryFn: fetchMetrics,
    enabled: queryEnabled,
    placeholderData,
  });

  return { data, ...rest };
};

export const useGetOverviewReviewsExplanationsSankey = ({
  cohortId,
  group,
  correlationId,
  enabled = true,
  sankeyGraphResponse,
}: REVIEWS_EXPLANATION_SANKEY_REQUEST_BODY) => {
  // Generate a stable query key
  const queryKey = useMemo(
    () => generateMetricsQueryKey({ cohortId, group, correlationId }),
    [cohortId, group, correlationId]
  );

  const isFeatureEnabled =
    checkOverviewExplanationsEnabled() &&
    checkOverviewReviewsExplanationsEnabled() &&
    !checkSandboxStaticDataFeaturesEnabled() &&
    !checkNewStaticDataFeaturesEnabled();
  const placeholderData = isFeatureEnabled ? undefined : null;

  // Memoize the enabled condition
  const queryEnabled = useMemo(() => isFeatureEnabled && enabled, [isFeatureEnabled, enabled]);

  const fetchMetrics = useCallback(
    () => fetchReviewsExplanationSankeyAPI({ cohortId, group, correlationId, sankeyGraphResponse }),
    [cohortId, group, correlationId, sankeyGraphResponse]
  );

  // Use the useQuery hook
  const { data, ...rest } = useQuery({
    queryKey,
    queryFn: fetchMetrics,
    enabled: queryEnabled,
    placeholderData,
  });

  return { data, ...rest };
};
